import React, { PureComponent, ReactNode } from 'react';
import { Range, Scale, TimeScale } from '../../../../common/graph/Scale';
import TimeUtils from '../../../../common/util/TimeUtils';
import GraphUtils from '../../../../common/graph/GraphUtils';
import { Axis, AxisOrientation, TimeAxis, TimeLabelFormat } from '../../../../common/graph/Axis';
import AutoSizer from 'react-virtualized-auto-sizer';
import './RegulationInteractionGraph.less';
import { RegulationOrMcdmOnlyDto, TimePeriod, TimeRange } from '../../../../model/CORE';
import TimePeriodUtils from '../../../../common/util/TimePeriodUtils';
import MeasureUtils from '../../../../common/util/MeasureUtils';
import AppContext from '../../../../common/context/AppContext';

const VERTICAL_AXIS_WIDTH = 0;
const HORZ_AXIS_HEIGHT = 30;
const MARGIN_TOP = 0;
const COUNT_MARGIN = 2;

export interface TimeSlice {
    timePeriod: TimePeriod;
    total: number;
    subTotals: Array<TimeSliceSubTotal>;
}

export interface TimeSliceSubTotal {
    regulationId: string;
    nbFlights: number;
}

interface RegulationInteractionGraphProps {
    timeRange: TimeRange;
    timeSlices: Array<TimeSlice>;
    colors: Map<string, string>; // Maps a regulationId to the provided color
    regulation: RegulationOrMcdmOnlyDto;
}

interface RegulationInteractionGraphState {}

/**
 * Renders a regulation interaction graph (either influencing or influenced graph).
 */
export class RegulationInteractionGraph extends PureComponent<
    RegulationInteractionGraphProps,
    RegulationInteractionGraphState
> {
    private svgRef = React.createRef<SVGSVGElement>();
    private timeScale: TimeScale;
    private countScale: Scale;

    state = {};
    renderBackground = () => {
        const tscale = this.timeScale.getScale();
        const cscale = this.countScale;
        return (
            <>
                <rect
                    fill="#3C3C3C"
                    x={tscale.source.to > tscale.source.from ? tscale.source.from : tscale.source.to}
                    y={cscale.source.to > cscale.source.from ? cscale.source.from : cscale.source.to}
                    width={
                        tscale.source.to > tscale.source.from
                            ? tscale.source.to - tscale.source.from
                            : tscale.source.from - tscale.source.to
                    }
                    height={
                        cscale.source.to > cscale.source.from
                            ? cscale.source.to - cscale.source.from
                            : cscale.source.from - cscale.source.to
                    }
                    shapeRendering="geometricPrecision"
                />
            </>
        );
    };
    renderTimeSlice = (timeSlice: TimeSlice) => {
        const tscale = this.timeScale;
        const cscale = this.countScale;

        let cumulatedNbFlights = 0;
        return (
            <svg key={`"timeslice-${timeSlice.timePeriod.from}"`}>
                {timeSlice.subTotals
                    .sort((one, other) => {
                        const colors = Array.from(this.props.colors.keys());
                        return colors.indexOf(one.regulationId) - colors.indexOf(other.regulationId);
                    })
                    .map((subTotal, idx) => {
                        const prevCumulatedNbFlights = cumulatedNbFlights;
                        cumulatedNbFlights += subTotal.nbFlights;
                        return (
                            <rect
                                key={`"subtotal-${idx}"`}
                                x={tscale.toSource(timeSlice.timePeriod.from)}
                                y={cscale.toSource(cumulatedNbFlights)}
                                width={
                                    tscale.toSource(timeSlice.timePeriod.to) -
                                    tscale.toSource(timeSlice.timePeriod.from)
                                }
                                height={cscale.toSource(prevCumulatedNbFlights) - cscale.toSource(cumulatedNbFlights)}
                                fill={this.props.colors?.get(subTotal.regulationId)}
                                shapeRendering="crispEdges"
                            />
                        );
                    })}
            </svg>
        );
    };
    renderBars = () => {
        if (!this.props.timeRange || !this.props.timeSlices) return undefined;

        return this.props.timeSlices.map(timeSlice => this.renderTimeSlice(timeSlice));
    };

    renderHorizontalGrid = interval => {
        const tscale = this.timeScale.getScale();
        const cscale = this.countScale;
        const range = cscale.target;
        let value = range.getFirstValue(interval);
        const increment = range.from <= range.to ? interval : -interval;
        let path = '';
        while (range.contains(value)) {
            const sourceVal = cscale.toSource(value);
            if (cscale.source.contains(sourceVal)) {
                path += ' M' + tscale.source.from + ',' + sourceVal + ' L' + tscale.source.to + ',' + sourceVal;
            }
            value += increment;
        }
        return <path d={path} stroke="#26262680" shapeRendering="crispEdges" />;
    };

    renderVerticalGrid = () => {
        if (!this.props.timeRange) return undefined;

        const interval = this.props.timeRange.stepSizeInMinutes * 60;

        const tscale = this.timeScale.getScale();
        const cscale = this.countScale;
        const range = tscale.target;
        let value = range.getFirstValue(interval);
        const increment = range.from <= range.to ? interval : -interval;
        let path = '';
        while (range.contains(value)) {
            const sourceVal = tscale.toSource(value);
            if (tscale.source.contains(sourceVal)) {
                path += ' M' + sourceVal + ',' + cscale.source.from + ' L' + sourceVal + ',' + cscale.source.to;
            }
            value += increment;
        }
        return <path d={path} stroke="#26262680" shapeRendering="crispEdges" />;
    };
    renderRegulationRate = () => {
        if (!this.props.regulation || !this.props.timeRange) return undefined;

        const tscale = this.timeScale;
        const cscale = this.countScale;

        const step = this.props.timeRange.stepSizeInMinutes;

        const graph = new Array<ReactNode>();
        let previousRate = 0;
        const regulationPeriods = MeasureUtils.getRegulationPeriods(this.props.regulation);
        for (let idx = 0; idx < regulationPeriods.constraints?.length; idx++) {
            const subPeriod = regulationPeriods.constraints[idx];
            const hourlyRate = MeasureUtils.getRate(subPeriod);
            const rate = (hourlyRate * step) / 60; // Rate is pro-rated to displayed slice duration
            graph.push([
                <line
                    key={'sub-period-transition-' + idx}
                    x1={tscale.toSource(subPeriod.timePeriod.from)}
                    y1={cscale.toSource(previousRate)}
                    x2={tscale.toSource(subPeriod.timePeriod.from)}
                    y2={cscale.toSource(rate)}
                    stroke="white"
                    strokeWidth={'2px'}
                    shapeRendering="geometricPrecision"
                />,
                <line
                    key={'sub-period-value-' + idx}
                    x1={tscale.toSource(subPeriod.timePeriod.from)}
                    y1={cscale.toSource(rate)}
                    x2={tscale.toSource(subPeriod.timePeriod.to)}
                    y2={cscale.toSource(rate)}
                    stroke="white"
                    strokeWidth={'2px'}
                    shapeRendering="geometricPrecision"
                />,
            ]);
            if (idx === regulationPeriods.constraints?.length - 1) {
                graph.push(
                    <line
                        key={'sub-period-transition-end'}
                        x1={tscale.toSource(subPeriod.timePeriod.to)}
                        y1={cscale.toSource(rate)}
                        x2={tscale.toSource(subPeriod.timePeriod.to)}
                        y2={cscale.toSource(0)}
                        stroke="white"
                        strokeWidth={'2px'}
                        shapeRendering="geometricPrecision"
                    />,
                );
            }
            previousRate = rate;
        }
        return graph;
    };
    renderCurrentTimeLine = () => {
        const tscale = this.timeScale;
        const cscale = this.countScale;
        return (
            <line
                x1={tscale.toSource(AppContext.getCurrentTime())}
                y1={cscale.source.from}
                x2={tscale.toSource(AppContext.getCurrentTime())}
                y2={cscale.source.to}
                stroke="#FFFFFF80"
                strokeWidth="1px"
                strokeDasharray="0.4em 0.4em"
                shapeRendering="crispEdges"
            />
        );
    };
    getCountTargetRange = () => {
        const maxNbFlights = this.props.timeSlices && Math.max(...this.props.timeSlices.map(slice => slice.total));
        // TODO: Take into account supplementary rate
        const regulationPeriods = MeasureUtils.getRegulationPeriods(this.props.regulation);
        const maxRate =
            regulationPeriods && Math.max(...regulationPeriods.constraints?.map(constraint => constraint.rate));

        const maxValue = Math.max(maxNbFlights, maxRate) || 0;
        return new Range(0, Math.max(15, maxValue + COUNT_MARGIN));
    };

    render() {
        const defaultTimeRange = AppContext.getMonitoringPeriod();

        const targetTimeRange = this.props.timeRange || defaultTimeRange;
        this.timeScale = TimeScale.newTimeScale(new Range(0, 100), targetTimeRange, null, 30);
        this.countScale = new Scale(new Range(100, 0), this.getCountTargetRange());

        let tickInterval;
        if (TimePeriodUtils.getDurationInMs(targetTimeRange) > 16 * TimeUtils.HR_TO_MS) {
            tickInterval = 120 * 60;
        } else if (TimePeriodUtils.getDurationInMs(targetTimeRange) > 8 * TimeUtils.HR_TO_MS) {
            tickInterval = 60 * 60;
        } else if (TimePeriodUtils.getDurationInMs(targetTimeRange) > 4 * TimeUtils.HR_TO_MS) {
            tickInterval = 30 * 60;
        } else {
            tickInterval = 20 * 60;
        }

        return (
            <AutoSizer>
                {({ height, width }) => {
                    this.timeScale.setSource(new Range(VERTICAL_AXIS_WIDTH, width));
                    this.countScale.source = new Range(height - HORZ_AXIS_HEIGHT, MARGIN_TOP);
                    const clip1Id = GraphUtils.getUniqueClipId();
                    return (
                        <div className="regulation-interaction-graph" style={{ height: height, width: width }}>
                            <svg ref={this.svgRef} width="100%" height="100%" style={{ overflow: 'visible' }}>
                                <defs>
                                    <clipPath id={clip1Id}>
                                        <rect
                                            x={VERTICAL_AXIS_WIDTH}
                                            y={0}
                                            width={width - VERTICAL_AXIS_WIDTH}
                                            height={height - HORZ_AXIS_HEIGHT}
                                        />
                                    </clipPath>
                                </defs>
                                <Axis
                                    scale={this.countScale}
                                    orientation={AxisOrientation.LEFT}
                                    height={height - HORZ_AXIS_HEIGHT - MARGIN_TOP}
                                    width={VERTICAL_AXIS_WIDTH}
                                    tickInterval={10}
                                    x={0}
                                    y={MARGIN_TOP}
                                />
                                <TimeAxis
                                    tickInterval={tickInterval}
                                    orientation={AxisOrientation.BOTTOM}
                                    labelFormat={TimeLabelFormat.HH_MM}
                                    timeScale={this.timeScale}
                                    width={width - VERTICAL_AXIS_WIDTH}
                                    height={HORZ_AXIS_HEIGHT}
                                    x={VERTICAL_AXIS_WIDTH}
                                    y={height - HORZ_AXIS_HEIGHT}
                                />
                                <g clipPath={`url(#${clip1Id})`}>
                                    {this.renderBackground()}
                                    {this.renderBars()}
                                    {this.renderHorizontalGrid(1)}
                                    {this.renderVerticalGrid()}
                                    {this.renderRegulationRate()}
                                    {this.renderCurrentTimeLine()}
                                </g>
                            </svg>
                        </div>
                    );
                }}
            </AutoSizer>
        );
    }
}
